import { Box, forwardRef } from '@chakra-ui/react';
import { isEqual, isFunction, union } from 'lodash';
import { useEffect, useState } from 'react';

import { TagSelectComponent } from './TagSelect.component';
import { formatTag } from './TagSelect.helpers';
import { TagSelectProps, Tags } from './TagSelect.types';

export const TagSelect = forwardRef<TagSelectProps, 'div'>(
  (
    {
      children,
      maxCharacters = 15,
      onChange,
      prefix = '',
      suffix = '',
      tagProps = {},
      tags: defaultTags,
      textInputPlaceholder = 'Create your own',
      textSubmitButton = 'Add Tag',
      ...boxProps
    },
    ref,
  ) => {
    const [tags, setTags] = useState<Tags>(defaultTags ?? []);
    const [value, setValue] = useState<string>('');

    const addTags = (addTags: Tags) => {
      const updatedTags = union(tags, addTags);
      setTags(updatedTags);
      onChange(updatedTags);
    };

    const removeTags = (removeTags: Tags) => {
      const updatedTags = [...tags].filter((tag) => !removeTags.includes(tag));
      setTags(updatedTags);
      onChange(updatedTags);
    };

    const handleChange = ({
      currentTarget,
    }: React.FormEvent<HTMLInputElement>) => {
      setValue(currentTarget.value);
    };

    const handleSubmit = () => {
      if (value.length) {
        addTags([formatTag({ tag: value, prefix, suffix })]);
        setValue('');
      }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
        handleSubmit();
      }
    };

    const handleAdd = (tag: string) => addTags([tag]);
    const handleRemove = (tag: string) => removeTags([tag]);

    const renderUI = () => (
      <TagSelectComponent
        maxCharacters={maxCharacters}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onRemove={handleRemove}
        onSubmit={handleSubmit}
        tagProps={tagProps}
        tags={tags || []}
        textInputPlaceholder={textInputPlaceholder}
        textSubmitButton={textSubmitButton}
        value={value}
      />
    );

    useEffect(() => {
      if (!isEqual(defaultTags, tags)) {
        setTags(defaultTags);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultTags]);

    return (
      <Box ref={ref} {...boxProps}>
        {isFunction(children)
          ? children({
              addTags,
              handleAdd,
              handleChange,
              handleKeyDown,
              handleRemove,
              handleSubmit,
              maxCharacters,
              removeTags,
              renderUI,
              setTags,
              setValue,
              tags,
              value,
            })
          : renderUI()}
      </Box>
    );
  },
);
